import type { SmartReminderType } from '@/services/smartReminders';

export type PlaybookActionType = 'call' | 'message' | 'navigate';

export type PlaybookStep = {
  text: string;
  actionLabel?: string;
  actionType?: PlaybookActionType;
  actionPayload?: { kind: 'message_collaborators' | 'open_maps_query' | 'open_url'; value: string };
};

export type Playbook = {
  id: string;
  title: string;
  icon: string; // Ionicons name
  familySafe: boolean;
  steps: PlaybookStep[];
};

export const PLAYBOOKS: Playbook[] = [
  {
    id: 'missed_connection',
    title: 'Missed connection',
    icon: 'airplane-outline',
    familySafe: true,
    steps: [
      { text: 'Take a breath and get everyone to a safe, calm spot near your gate or a help desk.' },
      { text: 'Check the airline app for rebooking options and standby availability.' },
      { text: 'If you have checked bags, confirm where they’ll end up and how to retrieve them if needed.' },
      {
        text: 'Let your group know you’re delayed and your new ETA once you have it.',
        actionLabel: 'Message collaborators',
        actionType: 'message',
        actionPayload: { kind: 'message_collaborators', value: "We missed a connection. Working on rebooking now — I'll share the new plan shortly." },
      },
    ],
  },
  {
    id: 'lost_item',
    title: 'Lost item',
    icon: 'briefcase-outline',
    familySafe: true,
    steps: [
      { text: 'Retrace your last 2–3 stops (restroom, café, rideshare, gate, hotel lobby).' },
      { text: 'Check “Find My” / location services if enabled, and mark the item as lost.' },
      { text: 'Report it: airline/hotel/front desk/lost-and-found. Note the case/reference number.' },
      { text: 'If it’s a phone, consider switching on lost mode and updating your lock-screen message.' },
    ],
  },
  {
    id: 'running_late',
    title: 'Running late',
    icon: 'time-outline',
    familySafe: true,
    steps: [
      { text: 'Decide: keep the next activity and trim the one after, or skip ahead to the most important stop.' },
      { text: 'If you have tickets or a reservation, check late-entry policy or call ahead.' },
      {
        text: 'Send a quick update to your group so everyone stays aligned.',
        actionLabel: 'Message collaborators',
        actionType: 'message',
        actionPayload: { kind: 'message_collaborators', value: "We’re running ~20 mins late. Let’s meet at the next stop — I’ll update if anything changes." },
      },
    ],
  },
  {
    id: 'need_pharmacy',
    title: 'Need a pharmacy / urgent care',
    icon: 'medkit-outline',
    familySafe: true,
    steps: [
      { text: 'For urgent symptoms or emergencies, use local emergency services immediately.' },
      { text: 'Search for a nearby pharmacy or urgent care and check hours before you go.' },
      {
        text: 'Open maps and search nearby options.',
        actionLabel: 'Open maps search',
        actionType: 'navigate',
        actionPayload: { kind: 'open_maps_query', value: 'pharmacy near me' },
      },
      { text: 'Bring IDs/insurance info if applicable and note any allergies/medications.' },
    ],
  },
];

